import { html, LitElement, TemplateResult } from 'lit';
import { property, query, queryAll, state } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { AccordionItems, CardItem, LandingPageEventsName, ProductItem, Themes } from '../types';
import { classMap } from 'lit/directives/class-map.js';
import { GzCountDownWebComponent } from '../../../../ui/gzCountDown';
import { InProductTrialCannotBeStartedReason } from '../../../b2b/types/InProductTrialCannotBeStartedReason';
import { GzAccordionWebComponent } from '../../../../ui/gzAccordion';
import { WEB_COMPONENT_GZ_ACCORDION } from '../../../../webcomponents.constants';
import { CountdownTranslations } from '../../../../ui/gzCountDown/types';
import { GzCardWebComponent } from '../../../../ui/gzCard';
import { CardWidthValues } from '../../../../ui/gzCard/types';
import { MspInProductTrialCannotBeStartedReason } from '../../../../company/common/mspInProductTrialsTab/types/msp-in-product-trials-types';

export class GzLandingPageWebComponent extends LitElement {
  @property({
    type: Object,
    attribute: false,
    reflect: false,
  })
  productItem: ProductItem | undefined;

  @property({
    type: Array,
    attribute: false,
    reflect: false,
  })
  cardItems: Array<CardItem>;

  @property({
    type: Array,
    attribute: false,
    reflect: false,
  })
  accordionItems: Array<AccordionItems>;

  @property({
    type: Boolean,
    attribute: false,
    reflect: false,
  })
  hasOutlineAroundAccordionItems = true;

  @property({
    type: Array,
    attribute: false,
    reflect: false,
  })
  accordionItemsFaq: Array<AccordionItems>;

  @property({ type: Object })
  texts = {
    title: '',
    faqTitle: '',
  };

  @property({ type: Number, attribute: true, reflect: true })
  dateToTimestamp = 0;

  @state()
  protected cannotBeStartedReason?: InProductTrialCannotBeStartedReason | MspInProductTrialCannotBeStartedReason;

  @state()
  private countdownTranslations?: CountdownTranslations;

  @queryAll(WEB_COMPONENT_GZ_ACCORDION)
  accordionsElements: GzAccordionWebComponent[];

  @query('.imageAccordion')
  imageAccordion: HTMLImageElement;

  static get styles() {
    return styles;
  }

  render() {
    // set classes
    const classes = {
      accordionElements: 'accordionElements',
      accordionElementsFaq: 'accordionElementsFaq',
    };

    // set image accordion classes
    const imageAccordionContainerClasses = classMap({
      'has-outline': this.hasOutlineAroundAccordionItems,
    });

    // set faq section classes
    const hasFaqSectionClass = classMap({
      hidden: !this.accordionItemsFaq || !this.accordionItemsFaq.length,
    });

    return html`
      <div class="wrapper d-flex flex-column">
        <div class="products-section d-flex flex-row">
          ${this.renderCountDown(this.countdownTranslations)} ${this.renderProduct()}
        </div>
        ${this.renderButtons()}
        <!---->
        ${this.renderBuyProductCard()}
        <div class="card-section">
          <div class="card-section-container layout-3-columns">${this.renderCards()}</div>
        </div>
        <gz-title class="section-title">${this.texts.title}</gz-title>
        <div class="container-section pb-5 pl-4 pr-4 d-flex flex-row">
          <div class="d-flex flex-column accordion-container mb-8">
            ${this.renderAccordions(this.accordionItems, classes.accordionElements)}
          </div>
          <div class="img-accordion-container ${imageAccordionContainerClasses}">
            <img
              class="imageAccordion"
              alt="placeholder"
              src="app/webcomponents/assets/images/trial/Placeholder_view_vector.png"
            />
          </div>
        </div>
        <div class="footer">
          <div class="d-flex flex-row pb-5 ${hasFaqSectionClass}">
            <div class="faq-image-container">
              <img class="faq-image" alt="placeholder" src="app/webcomponents/assets/images/trial/FAQ.svg" />
            </div>
            <div class="faq-section">
              <gz-title class="pb-2">${this.texts.faqTitle}</gz-title>
              ${this.renderAccordions(this.accordionItemsFaq, classes.accordionElementsFaq)}
            </div>
          </div>
        </div>
      </div>
    `;
  }

  protected renderButtons(): TemplateResult | undefined {
    return undefined;
  }

  protected renderBuyProductCard(): TemplateResult | undefined {
    return undefined;
  }

  protected renderProduct(): TemplateResult | undefined {
    if (!this.productItem) {
      return;
    }
  }

  private renderCards(): GzCardWebComponent[] | undefined {
    if (!this.cardItems) {
      return;
    }

    return this.cardItems.map(item => {
      const card = new GzCardWebComponent();
      card.cardWidth = CardWidthValues.NORMAL;
      card.name = item.cardName;
      card.icon = item.icon;

      // set card content
      const cardContent = document.createElement('div');
      cardContent.classList.add('card-description');
      cardContent.innerHTML = item.text;
      card.setCardContent(cardContent);

      return card;
    });
  }

  private renderAccordions(data: Array<AccordionItems>, className: string): TemplateResult[] | undefined {
    if (!data) {
      return;
    }

    return data.map(item => {
      // create accordion component
      const accordionComponent = new GzAccordionWebComponent();
      accordionComponent.accordionTitle = item.title;
      accordionComponent.innerHTML += item.description;
      accordionComponent.isOpen = item.isOpen ?? false;

      if (item.image) {
        accordionComponent.setAttribute('imageSrc', item?.image.src);
        accordionComponent.setAttribute('imageAlt', item?.image.alt);

        if (item.isOpen) {
          this.imageAccordion.setAttribute('src', item?.image.src);
        }
      }

      // add class to component
      accordionComponent.classList.add(className);

      accordionComponent.addEventListener('open', (Event: CustomEvent) => {
        this.closeAllAccordions(className);
        this.openCurrentAccordion(Event);
      });

      // hide image on close event
      accordionComponent.addEventListener('close', () => {
        //things to do on close
      });

      return html` ${accordionComponent} `;
    });
  }

  private closeAllAccordions(className: string): void {
    this.accordionsElements.forEach(item => {
      // close accordions from same class
      if (item.classList.contains(className)) {
        item.isOpen = false;
      }
    });
  }

  private openCurrentAccordion(Event: CustomEvent): void {
    const target = Event.target as GzAccordionWebComponent;
    const imageSrc = target.getAttribute('imagesrc');
    target.isOpen = true;

    if (imageSrc !== null) {
      this.imageAccordion.setAttribute('src', imageSrc);
    }
  }

  protected createCountDownWebComponent(translations?: CountdownTranslations) {
    const component = new GzCountDownWebComponent();
    component.calendarStyle = true;
    component.dateToTimestamp = this.dateToTimestamp;
    component.startTimer();

    if (translations) {
      component.translations = translations;
      return component;
    }
  }

  protected renderCountDown(translations?: CountdownTranslations): TemplateResult | undefined {
    const component = this.createCountDownWebComponent(translations);
    return html`${component}`;
  }

  protected handleProductClicked(event: Event): void {
    if (!event.target) {
      return;
    }

    const clickEvent = new CustomEvent(LandingPageEventsName.PRODUCT_ITEM, {
      detail: {
        target: event.target,
        type: this.productItem?.type,
      },
      bubbles: true,
      composed: true,
    });

    this.dispatchEvent(clickEvent);
  }

  public setCountdownTranslations(countdownTranslations?: CountdownTranslations): void {
    this.countdownTranslations = countdownTranslations;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    gzLandingPage: GzLandingPageWebComponent;
  }
}
